/**
 * Agenda : rendez-vous, leurs états et leur origine.
 *
 * Ensembles fermés partagés entre la base (pgEnum), le serveur (zod) et le front (libellés i18n).
 * Règle : valeurs ASCII en snake_case, jamais de libellé humain ici. Ce fichier est réexporté par
 * `../enums.js`, qui reste le seul point d'entrée : personne n'importe un domaine directement.
 */

/**
 * V1 avait six statuts en capitales, dont `REJECTED` que rien ne distinguait de `CANCELLED`
 * (docs/audit/audit-schema.md §6). La V2 en garde cinq, en snake_case ASCII.
 */
export const appointmentStatuses = [
  'pending',
  'confirmed',
  'completed',
  'cancelled',
  'no_show',
] as const;
export type AppointmentStatus = (typeof appointmentStatuses)[number];

/**
 * Machine à états du rendez-vous, partagée : le serveur la fait respecter, le sélecteur de statut
 * du dialogue d'agenda n'affiche que les cibles atteignables. Seul `completed` est terminal : une
 * annulation ou une absence notée par erreur revient à `confirmed`. Le serveur ajoute ses propres
 * refus au cas par cas (`appointments.status-rules.ts`), la machine ne dit que l'atteignable.
 */
export const allowedAppointmentStatusTransitions: Record<
  AppointmentStatus,
  readonly AppointmentStatus[]
> = {
  pending: ['confirmed', 'cancelled'],
  confirmed: ['completed', 'cancelled', 'no_show'],
  completed: [],
  cancelled: ['confirmed'],
  no_show: ['confirmed'],
};

/** `note` : post-it sur le créneau. `appointment` : rendez-vous ordinaire. */
export const appointmentKinds = ['appointment', 'note'] as const;
export type AppointmentKind = (typeof appointmentKinds)[number];

/** Origine du rendez-vous. `app` = saisi dans l'agenda ; les deux derniers viennent d'une synchronisation. */
export const appointmentSources = ['app', 'booking_widget', 'doctena', 'docalia'] as const;
export type AppointmentSource = (typeof appointmentSources)[number];

/**
 * Délai, en heures, entre le courriel de rappel et le rendez-vous. Liste fermée : « la veille » ou
 * « l'avant-veille ». La base la recopie dans une contrainte, l'écran en fait son sélecteur.
 */
export const appointmentReminderLeadHours = [24, 48] as const;
export type AppointmentReminderLeadHours = (typeof appointmentReminderLeadHours)[number];
export const APPOINTMENT_REMINDER_DEFAULT_LEAD_HOURS: AppointmentReminderLeadHours = 24;

/**
 * Mode des statistiques affichées en en-tête de l'agenda. `marked` reprend le trio de la V1
 * (à venir / aujourd'hui / prochain), `day` compte par journée, `halfday` par demi-journée.
 */
export const agendaStatsModes = ['marked', 'day', 'halfday'] as const;
export type AgendaStatsMode = (typeof agendaStatsModes)[number];

/* -------------------------------------------------------------------------- */
/* Rendez-vous récurrents                                                      */
/* -------------------------------------------------------------------------- */

/**
 * Cadence d'une série. `biweekly` n'est pas redondant avec `weekly` + intervalle 2 dans
 * l'interface : le praticien choisit « toutes les deux semaines » d'un clic, et peut encore
 * l'espacer avec l'intervalle. Le générateur en fait un pas de `14 × intervalle` jours.
 */
export const recurrenceFrequencies = ['daily', 'weekly', 'biweekly', 'monthly', 'yearly'] as const;
export type RecurrenceFrequency = (typeof recurrenceFrequencies)[number];

/** Fin d'une série : jamais, à une date, ou après un nombre d'occurrences. */
export const recurrenceEndModes = ['never', 'on_date', 'after'] as const;
export type RecurrenceEndMode = (typeof recurrenceEndModes)[number];

/**
 * Portée d'une action sur une occurrence. `following` scinde la série à ce rang ; `all` ne touche
 * jamais le passé — une séance déjà honorée ne se réécrit pas.
 */
export const recurrenceScopes = ['this', 'following', 'all'] as const;
export type RecurrenceScope = (typeof recurrenceScopes)[number];

/**
 * Sort du rang libéré par la suppression d'une occurrence, sur une série « après N séances ».
 * `count` : le rang reste consommé, le patient aura N − 1 séances. `release` : le rang est rendu,
 * la série produit une séance de plus au bout pour arriver à N. Sans effet sur une série qui
 * s'arrête à une date ou jamais, où c'est le calendrier qui décide de la fin.
 */
export const recurrenceDeleteHandlings = ['count', 'release'] as const;
export type RecurrenceDeleteHandling = (typeof recurrenceDeleteHandlings)[number];

/** Regroupement des motifs dans le sélecteur du formulaire de consultation (écran « Motifs »). */
export const consultationReasonCategories = [
  'douleurs',
  'troubles_fonctionnels',
  'prevention',
  'autres',
] as const;
export type ConsultationReasonCategory = (typeof consultationReasonCategories)[number];

/**
 * Lieu d'un acte du catalogue tarifaire. `none` = aucun lieu (séance non honorée, frais, etc.).
 * Les libellés humains (« Cabinet », « Domicile », « Aucun ») vivent dans les traductions.
 */
export const consultationTypePlaces = ['cabinet', 'domicile', 'none'] as const;
export type ConsultationTypePlace = (typeof consultationTypePlaces)[number];
